import React, { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "../../components/Header/Header";
import { PredictionsTableSection } from "./sections/PredictionsTableSection/PredictionsTableSection";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PREDICTIONS_PAGE_LIMIT } from '../../constants';

import mobilebg from "../../assets/mobilebg.png";
import dashboardCover from "../../assets/dashboardcover.jpg"
export const MyPredictions = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = PREDICTIONS_PAGE_LIMIT;

  // Add filter state
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");

  // Handle return page from navigation state using useLayoutEffect for synchronous update
  useLayoutEffect(() => {
    const state = location.state as { returnPage?: number };
    if (state?.returnPage) {
      setCurrentPage(state.returnPage);
      // Clear the state to prevent unwanted page jumps on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Calculate total pages based on total items and limit
  const totalPages = Math.ceil(totalItems / limit);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Maximum number of page numbers to show

    if (totalPages <= maxVisiblePages) {
      // If total pages is less than max visible, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Calculate start and end of visible pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at the start
      if (currentPage <= 2) {
        endPage = 4;
      }
      // Adjust if at the end
      if (currentPage >= totalPages - 1) {
        startPage = totalPages - 3;
      }

      // Add ellipsis if needed
      if (startPage > 2) {
        pageNumbers.push(t('ellipsis'));
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push(t('ellipsis'));
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handleMyPredictions = () => {
    navigate(`/${country}/my-predictions`);
  };

  return (
    <div>
      <div 
        data-testid="mobile-background"
        className="block sm:hidden bg-mobile-landing-gradient inset-0 fixed bottom-[50%] z-0"
      />
      <Header className="border-none sm:border-b bg-none sm:bg-custom-gradient shadow-none sm:shadow-custom"/>
    
    <div className="bg-transparent sm:bg-[#fcfcfc] flex flex-row justify-center w-full bg-no-repeat bg-contain bg-bottom sm:bg-none sm:bg-white"
     style={{ 
      backgroundImage: `url(${mobilebg})`
    }}>
      <div className="bg-transparent sm:bg-[#fcfcfc] w-full relative flex flex-col">
          {/* Sidebar */}
          <div 
            data-testid="sidebar"
            className="fixed left-0 top-[60px] hidden flex-col w-[100px] h-screen items-start gap-5 p-2.5 bg-primary-new500 sm:flex"
          >
            <div 
              data-testid="prediction-icon-container"
              className="cursor-pointer relative w-20 h-20 bg-[#014224] rounded-[10px] flex items-center justify-center"
              onClick={handleMyPredictions}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleMyPredictions();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={t('myPredictions')}
            >
              <img
                className="w-11 h-11"
                alt={t('predictions')}
                src="https://c.animaapp.com/m9qofkimNTaxBx/img/icon-prediction.svg"
              />
            </div>
          </div>

        {/* Main content */}
        <div 
          data-testid="main-content"
          className="sm:ml-[100px] w-full sm:w-[calc(100%-100px)]"
        >
            
          <div 
            data-testid="content-container"
            className="h-[calc(100dvh-180px)] sm:h-[calc(100dvh-120px)]  overflow-auto py-4 sm:py-0 pb-4"
          >
            {/* Banner image */}
            <div className="w-full px-6">
              <img
                className="w-full h-auto object-cover"
                alt="dashboardCover"
                src={dashboardCover}
              />
            </div>
            <PredictionsTableSection 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onTotalItemsChange={setTotalItems}
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
              selectedDateFilter={selectedDateFilter}
              setSelectedDateFilter={setSelectedDateFilter}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              globalSearchQuery={globalSearchQuery}
              setGlobalSearchQuery={setGlobalSearchQuery}
            />
            
          </div>
          <div className="bg-[#fff] shadow-pagination-shadow h-[60px] w-full flex items-center justify-end px-6">
            {/* Pagination */}
            <div className="flex items-center justify-center gap-2 w-auto">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={t('previousPage')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              {getPageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === t('ellipsis') ? (
                    <span className="px-2">{t('ellipsis')}</span>
                  ) : (
                    <button
                      onClick={() => handlePageChange(page as number)}
                      className={`w-8 h-8 rounded-md ${
                        currentPage === page
                          ? 'bg-[#0c612c] text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      aria-label={`${t('page')} ${page} ${t('of')} ${totalPages}`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}
              
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={t('nextPage')}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};